import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import {
  ButtonGroup,
  Col,
  Form,
  FormFeedback,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Row,
} from "reactstrap";
import {isUndefined, omit} from "lodash";
import apiUser from "../../../apiUser";
import {toast} from "react-toastify";
import Button from "../../Button";
import InputMaskNip from "../../InputMaskNip";
import InputMaskPostalCode from "../../InputMaskPostalCode";
import VehicleSelect from "../../Vehicle/VehicleSelect";
import WorkshopProductPriceGroupSelect from "../../WorkshopProductPriceGroup/WorkshopProductPriceGroupSelect";
import storeInit from "../../../storeInit";
import classnames from 'classnames';
import InputNumberFloat from "../../Application/InputNumberFloat";
import ApplicationSwitch from "../../Application/ApplicationSwitch";
import CountrySelect from "../../CountrySelect";
import getWorkshop from "../../../utils/getWorkshop";
import workshopSettingsIsEnabled from "../../../utils/workshopSettingsIsEnabled";
import AppFormCancelButton from "../../../componentsReusable/AppFormCancelButton";
import PhoneNumber from "../../PhoneNumber";
import {withTranslation} from "react-i18next";
import PaymentMethodSelect from "../../Application/PaymentMethodSelect";
import PaymentDaysSelect from "../../Application/PaymentDaysSelect";

class ClientForm extends PureComponent {
  constructor(props) {
    super(props);

    this.store = storeInit.getState();

    this.state = {
      errors: {},
      saving: false,
      data: omit(isUndefined(props.data) ? this.getDataDefault() : props.data, ['id', 'createdAt', 'updatedAt']),
    };

    this.onChangeData = this.onChangeData.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  getDataDefault(){
    return {
      id: null,
      description: null,
      type: 'company',
      email: '',
      phoneNumber: {
        number: '',
        countryCode: getWorkshop().workshopDetails.countryCode,
      },
      name: '',
      nip: '',
      countryCode: getWorkshop().workshopDetails.countryCode,
      city: '',
      postalCode: '',
      street: '',
      discountJobs: 0.0,
      discountParts: 0.0,
      marketingAgreement: true,
      paymentMethod: null,
      paymentDays: null,
    }
  }

  onChangeData(obj){
    const {data} = this.state;

    this.setState({
      data: {
        ...data,
        ...obj,
      }
    })
  }

  onSubmit(){
    const {data} = this.state;

    this.setState({
      saving: true,
      errors: {},
    });

    if(this.props.onSavingStart){
      this.props.onSavingStart();
    }

    if(!this.props?.data?.id){
      apiUser.clients.post(data)
        .then(data => {
          this.setState({
            saving: false,
          });

          if(this.props.onSavingStop){
            this.props.onSavingStop();
          }

          this.props.onCreated(data);

          toast.success(this.props.t('created'));
        })
        .catch(res => {
          this.setState({
            saving: false,
            errors: res?.response?.data?.children ?? {}
          });

          if(this.props.onSavingStop){
            this.props.onSavingStop();
          }
        });
    }else{
      apiUser.clients.patch(this.props?.data?.id, omit(data, ['type']))
        .then(data => {
          this.setState({
            saving: false,
          });

          if(this.props.onSavingStop){
            this.props.onSavingStop();
          }

          toast.success(this.props.t('common:data_has_been_saved'));
        })
        .catch(res => {
          this.setState({
            saving: false,
            errors: res?.response?.data?.children ?? {}
          });

          if(this.props.onSavingStop){
            this.props.onSavingStop();
          }
        });
    }
  }

  render() {
    const {data, saving, errors} = this.state;
    const {submitButton} = this.props;

    return (
      <Form onSubmit={e => e.preventDefault()}>
        {!this.props?.data?.id && <div className="d-flex flex-nowrap align-items-top justify-content-center mb-4">
          <ButtonGroup>
            <Button
              size="sm"
              className={classnames({'disabled': data.type !== 'person'})}
              color={data.type === 'person' ? 'primary' : 'secondary'}
              onClick={() => this.onChangeData({type: 'person'})}
            >{this.props.t('type_person')}</Button>
            <Button
              size="sm"
              className={classnames({'disabled': data.type !== 'company'})}
              color={data.type === 'company' ? 'primary' : 'secondary'}
              onClick={() => this.onChangeData({type: 'company'})}
            >{this.props.t('type_company')}</Button>
          </ButtonGroup>
        </div>}
        <Row>
          <Col className="mb-2 mb-md-0" xs={12} md={6}>
            {data.type === 'company' && <div className="d-flex flex-nowrap align-items-top mb-2">
              <div className="flex-grow-1 pr-1">
                <Input
                  invalid={!!errors.name?.errors}
                  placeholder={this.props.t('field.name')}
                  onChange={e => this.onChangeData({name: e.target.value})}
                  value={data.name}
                />
                <FormFeedback>{errors.name?.errors?.join('; ')}</FormFeedback>
              </div>
              <div className="pl-1" style={{width: '183px'}}>
                <InputMaskNip
                  countryCode={data.countryCode}
                  number={data.nip}
                  onChange={({number}) => this.onChangeData({nip: number})}
                  onFindGusData={gusData => {
                    this.setState({
                      data: {
                        ...data,
                        name: gusData.name,
                        city: gusData.city,
                        postalCode: gusData.zipCode,
                        street: [gusData.street ? gusData.street : gusData.city, [gusData.propertyNumber, gusData.apartmentNumber].filter(v => !!v).join(' / ')].join(' ')
                      }
                    })
                  }}
                />
                {errors.nip?.errors &&
                <FormFeedback className="d-block">{errors.nip?.errors?.join('; ')}</FormFeedback>}
              </div>
            </div>}
            <div className="d-flex flex-nowrap align-items-top">
              <div className="w-50 pr-1">
                <Input invalid={!!errors.firstname?.errors} placeholder={this.props.t('field.firstname')} onChange={e => this.onChangeData({firstname: e.target.value})} value={data.firstname} />
                <FormFeedback>{errors.firstname?.errors?.join('; ')}</FormFeedback>
              </div>
              <div className="w-50 pl-1">
                <Input invalid={!!errors.lastname?.errors} placeholder={this.props.t('field.lastname')} onChange={e => this.onChangeData({lastname: e.target.value})} value={data.lastname} />
                <FormFeedback>{errors.lastname?.errors?.join('; ')}</FormFeedback>
              </div>
            </div>
            <div className="d-flex flex-nowrap align-items-top mt-2">
              <div className="pr-1" style={{width: '120px'}}>
                <InputMaskPostalCode
                  countryCode={data.countryCode}
                  invalid={!!errors.postalCode?.errors}
                  value={data.postalCode}
                  onChange={postalCode => this.onChangeData({postalCode})}
                />
                <FormFeedback>{errors.postalCode?.errors?.join('; ')}</FormFeedback>
              </div>
              <div className="flex-grow-1 pl-1">
                <Input invalid={!!errors.city?.errors} disabled={!!data.id && !!this.props.data.city} placeholder={this.props.t('field.city')} onChange={e => this.onChangeData({city: e.target.value})} value={data.city} />
                <FormFeedback>{errors.city?.errors?.join('; ')}</FormFeedback>
              </div>
              <div className="pl-1" style={{width: '145px'}}>
                <CountrySelect
                  value={data.countryCode}
                  onChange={countryCode => {
                    const obj = {
                      countryCode,
                      phoneNumber: {
                        ...data.phoneNumber,
                        countryCode
                      }
                    }

                    this.onChangeData(obj)
                  }}
                />
                <FormFeedback>{errors.city?.errors?.join('; ')}</FormFeedback>
              </div>
            </div>
            <div className="d-flex flex-nowrap align-items-top mt-2">
              <div className="w-100">
                <Input invalid={!!errors.street?.errors} disabled={!!data.id && !!this.props.data.street} placeholder={this.props.t('field.street')} onChange={e => this.onChangeData({street: e.target.value})} value={data.street} />
                <FormFeedback>{errors.street?.errors?.join('; ')}</FormFeedback>
              </div>
            </div>
            <div className="d-flex flex-row flex-wrap flex-two-cols mt-2">
              <div>
                <PhoneNumber
                  onChange={phoneNumber => this.onChangeData({phoneNumber})}
                  value={data.phoneNumber}
                />
              </div>
              <InputGroup>
                <InputGroupAddon addonType="prepend"><InputGroupText><i className="fa fa-at" /></InputGroupText></InputGroupAddon>
                <Input invalid={!!errors.email?.errors} placeholder={this.props.t('field.email')} onChange={e => this.onChangeData({email: e.target.value})} value={data.email} />
                <FormFeedback>{errors.email?.errors?.join('; ')}</FormFeedback>
              </InputGroup>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div className="d-flex flex-row flex-wrap flex-two-cols">
              <div>
                <VehicleSelect
                  placeholder={this.props.t('field.vehicle_default')}
                  onChange={vehicleDefault => {
                    this.onChangeData({vehicleDefault})
                  }}
                  vehicle={data?.vehicleDefault?.id} />
              </div>
              <div>
                <PaymentMethodSelect isClearable={true} value={data.paymentMethod} onChange={paymentMethod => this.onChangeData({paymentMethod})}/>
                <FormFeedback>{errors.paymentMethod?.errors?.join('; ')}</FormFeedback>
              </div>
              <InputGroup size={'md'}>
                <PaymentDaysSelect isClearable={true}
                                   value={data.paymentDays}
                                   onChange={paymentDays => this.onChangeData({paymentDays})}
                />
                <FormFeedback>{errors.paymentDays?.errors?.join('; ')}</FormFeedback>
              </InputGroup>

              {getWorkshop().workshopProductPriceGroups.length > 1 && <div>
                <WorkshopProductPriceGroupSelect
                  tooltip
                  onChange={workshopProductPriceGroup => this.onChangeData({workshopProductPriceGroup})}
                  workshopProductPriceGroup={data?.workshopProductPriceGroup}/>
              </div>}
              {workshopSettingsIsEnabled('discounts') && <React.Fragment>
                <div>
                  <InputNumberFloat
                    value={data.discountJobs}
                    size="md"
                    digits={2}
                    min={0}
                    errors={errors.discountJobs?.errors}
                    max={99.99}
                    appendText={this.props.t('field.discount_jobs')}
                    onChange={discountJobs => this.onChangeData({discountJobs})}
                  />
                </div>
                <div>
                  <InputNumberFloat
                    value={data.discountParts}
                    size="md"
                    digits={2}
                    min={0}
                    errors={errors.discountParts?.errors}
                    max={99.99}
                    appendText={this.props.t('field.discount_parts')}
                    onChange={discountParts => this.onChangeData({discountParts})}
                  />
                </div>
              </React.Fragment>}
              <div className="w-100">
                <Input type={'textarea'} rows={5} placeholder={this.props.t('field.description')} onChange={e => this.onChangeData({description: e.target.value})} value={data.description} />
                <FormFeedback>{errors.description?.errors?.join('; ')}</FormFeedback>
              </div>
              <div>
                <div className="d-flex justify-content-between align-items-center">
                  <Label className="mb-0 pr-2 d-none d-md-block">{this.props.t('field.marketing_agreement')}</Label>
                  <Label className="mb-0 pr-2 d-md-none">{this.props.t('field.marketing_agreement_short')}</Label>
                  <div style={{ width: '50px' }}>
                    <ApplicationSwitch
                      onChange={e => this.onChangeData({marketingAgreement: e.target.checked})}
                      color={'success'}
                      label
                      checked={data.marketingAgreement}/>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {submitButton && <Row className="mt-3">
          <Col className="d-flex justify-content-end">
            <AppFormCancelButton />
            <Button
              className="ml-3"
              loading={saving}
              onClick={this.onSubmit}
            >
              <i className="fas fa-save" />
              <span>{this.props.t('common:btn.save')}</span>
            </Button>
          </Col>
        </Row>}
      </Form>
    );
  }
}

ClientForm.defaultProps = {
  submitButton: true,
};

ClientForm.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.number,
    description: PropTypes.string,
    type: PropTypes.oneOf(['person', 'company']).isRequired,
    email: PropTypes.string,
    phoneNumber: PropTypes.shape({
      countryCode: PropTypes.string,
      number: PropTypes.string,
    }),
    name: PropTypes.string,
    nip: PropTypes.string,
    postalCode: PropTypes.string,
    countryCode: PropTypes.string,
    city: PropTypes.string,
    street: PropTypes.string,
    discountJobs: PropTypes.number,
    discountParts: PropTypes.number,
    marketingAgreement: PropTypes.bool,
    paymentDays: PropTypes.number,
    paymentMethod: PropTypes.number
  }),
  submitButton: PropTypes.bool.isRequired,
  onCreated: PropTypes.func.isRequired,
  onSavingStart: PropTypes.func,
  onSavingStop: PropTypes.func,
};

export default withTranslation('client', {withRef:true})(ClientForm);
